import 'reflect-metadata';
import { CustomEnum } from '../../types/customEnum';

export interface IJsonMetaData<T> {
  name?: string;
  clazz?: new () => T;
}

const jsonMetadataKey = 'jsonProperty';

export function JsonProperty<T>(metadata?: IJsonMetaData<T> | string): any {
  if (metadata instanceof String || typeof metadata === 'string') {
    return Reflect.metadata(jsonMetadataKey, {
      name: metadata,
      clazz: undefined,
    });
  } else {
    const metadataObj = metadata as IJsonMetaData<T>;
    return Reflect.metadata(jsonMetadataKey, {
      name: metadataObj ? metadataObj.name : undefined,
      clazz: metadataObj ? metadataObj.clazz : undefined,
    });
  }
}

export function getClazz(target: any, propertyKey: string): any {
  return Reflect.getMetadata('design:type', target, propertyKey);
}

export function getJsonProperty<T>(
  target: any,
  propertyKey: string
): IJsonMetaData<T> {
  return Reflect.getMetadata(jsonMetadataKey, target, propertyKey);
}

export class MappingService {
  static isPrimitive(obj: any) {
    switch (typeof obj) {
      case 'string':
      case 'number':
      case 'boolean':
        return true;
    }

    return !!(
      obj instanceof String ||
      obj === String ||
      obj instanceof Number ||
      obj === Number ||
      obj instanceof Boolean ||
      obj === Boolean
    );
  }

  static isArray(object: any) {
    if (object === Array) {
      return true;
    } else if (typeof Array.isArray === 'function') {
      return Array.isArray(object);
    } else {
      return !!(object instanceof Array);
    }
  }

  static isDate(o: any) {
    return o === Date;
  }

  static isCustomEnum(o: any) {
    return o.prototype instanceof CustomEnum;
  }

  static deserialize<T extends {}>(clazz: new () => T, jsonObject: any) {
    const obj = new clazz();
    Object.keys(obj).forEach(key => {
      const propertyMetadataFn: (
        IJsonMetaData: any
      ) => any = propertyMetadata => {
        const propertyName = propertyMetadata.name || key;
        const innerJson = jsonObject ? jsonObject[propertyName] : undefined;
        const clazz = getClazz(obj, key);
        if (MappingService.isArray(clazz)) {
          const metadata = getJsonProperty(obj, key);
          if (metadata.clazz || MappingService.isPrimitive(clazz)) {
            if (innerJson && MappingService.isArray(innerJson)) {
              return innerJson.map((item: any) =>
                MappingService.deserialize(metadata.clazz as new () => {}, item)
              );
            } else {
              return undefined;
            }
          } else {
            return innerJson;
          }
        } else if (MappingService.isCustomEnum(clazz)) {
          let val;
          Object.keys(clazz).forEach(member => {
            if (member === innerJson) {
              val = clazz[member];
            }
          });
          return val;
        } else if (MappingService.isDate(clazz) && jsonObject != null) {
          return new Date(jsonObject[propertyName]);
        } else if (!MappingService.isPrimitive(clazz)) {
          return MappingService.deserialize(clazz, innerJson);
        } else {
          return jsonObject ? jsonObject[propertyName] : undefined;
        }
      };

      const propertyMetadata = getJsonProperty(obj as any, key as string);
      if (propertyMetadata) {
        obj[key] = propertyMetadataFn(propertyMetadata);
      } else {
        if (jsonObject && jsonObject[key] !== undefined) {
          obj[key] = jsonObject[key];
        }
      }
    });
    return obj;
  }
}
